/**
 * ChatMessageRow Component
 * 
 * Individual message bubble with proper metadata display.
 * - Self messages: Right-aligned, green bubble, timestamp below
 * - Other messages: Left-aligned, avatar left of bubble, name + timestamp above bubble, green bubble
 * 
 * Architecture: Follows original ChatMessage.tsx layout, updated per user feedback
 * Part of Issue #608: Chat UX improvements
 */

import { type Message } from './ChatMessage';
import Avatar from './Avatar';
import { ChatDateTime } from './ui/DateTimeDisplay';

interface ChatMessageRowProps {
  message: Message;
  isOwn: boolean;
}

export function ChatMessageRow({ message, isOwn }: ChatMessageRowProps) {
  const isOther = !isOwn;
  
  return (
    <div className={`flex gap-2 mb-2 px-3 ${isOther ? 'justify-start' : 'justify-end'}`}>
      {/* Avatar for other users only */}
      {isOther && (
        <div className="shrink-0 mt-1">
          <Avatar
            size="sm"
            name={message.sender || 'User'}
            profile_image_url={message.avatarUrl}
          />
        </div>
      )}
      
      {/* Message container */}
      <div className={`flex flex-col ${isOther ? 'items-start' : 'items-end'} max-w-prose`}>
        {/* Sender name and timestamp for other users */}
        {isOther && message.sender && (
          <div className="flex items-center justify-between gap-2 px-1 mb-1 w-full">
            <span className="text-sm text-text-secondary font-medium">
              {message.sender}
            </span>
            <span className="text-xs text-text-muted shrink-0">
              {message.timestampSeconds ? (
                <ChatDateTime 
                  timestamp={message.timestampSeconds}
                  className="text-xs"
                />
              ) : (
                message.timestamp
              )}
            </span>
          </div>
        )}
        
        {/* Image message */}
        {message.imageUrl && (
          <div
            className="overflow-hidden mb-1 border border-oro-border rounded-2xl"
            style={{
              borderBottomLeftRadius: isOther ? '0' : '1rem',
              borderBottomRightRadius: isOther ? '1rem' : '0'
            }}
          >
            <img
              src={message.imageUrl}
              alt="Shared image"
              className="max-w-full h-auto"
            />
          </div>
        )}
        
        {/* Text bubble */}
        {message.content && (
          <div
            className={`px-4 py-3 text-text-primary shadow-soft rounded-2xl ${
              isOther ? 'bg-oro-chat-bubble-other-bg' : 'bg-oro-chat-bubble-self-bg'
            }`}
            style={{
              borderBottomLeftRadius: isOther ? '0' : '1rem',
              borderBottomRightRadius: isOther ? '1rem' : '0'
            }}
          >
            <p className="whitespace-pre-wrap wrap-break-word">
              {message.content}
            </p>
          </div>
        )}
        
        {/* Timestamp (only shown for self messages) */}
        {!isOther && (
          <span className="px-1 mt-1 text-xs text-text-muted">
            {message.timestampSeconds ? (
              <ChatDateTime 
                timestamp={message.timestampSeconds}
                className="text-xs"
              />
            ) : (
              message.timestamp
            )}
          </span>
        )}
      </div>
    </div>
  );
}
